<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import Card from '@uiKit/components/Card.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import ConfirmLeaveModal from '@uiKit/components/ConfirmLeaveModal.vue'
import FilterCardRow from '@uiKit/components/FilterCardRow.vue'
import ListTableCard, { type ListTableState } from '@uiKit/components/ListTableCard.vue'
import { routes } from '@app/router/routes'
import ListingRequestRow from '../components/list/ListingRequestRow.vue'
import { useListingRequestsPageQuery } from '../api/useListingRequestsPageQuery'
import { useListingRequestStatusCountsQuery } from '../api/useListingRequestStatusCountsQuery'
import { useDraftDiscard } from '../composables/useDraftDiscard'
import { LISTINGS_PAGE_LIMIT, LISTING_STATUS_KPIS } from '../constants/listingRequestLifecycle'
import { LISTINGS_GRID_TEMPLATE, LISTINGS_TABLE_COLUMNS } from '../constants/listingsTable'
import { useListingStatusFilter } from '../composables/useListingStatusFilter'
import { countListingStatusKpi } from '../utils/listingStatusKpiUtils'
import type { ListingRequestListPage } from '../types/listingRequestTypes'

// Smart container for the Sell Request table: owns the numbered-page query, the
// KPI-card status filter (server-side) and its counts, the search term, and routing.
// Laid out like the Properties and Marketplace lists — filter cards, then the
// shared list card with the toolbar, table and pager.
//
// Search is client-side over the page on screen — the list endpoint has no
// `search` param yet. The no-match state says so, rather than implying the
// seller has no such request at all. When seller-service adds the param, move
// `searchTerm` into the query key and drop `visibleRows`' filter.
const router = useRouter()
const page = ref(1)
const searchTerm = ref('')
const statusFilter = useListingStatusFilter(page)
const { label: statusFilterLabel, isFiltered, clear: clearStatusFilter } = statusFilter
const selectedKpi = computed({
  get: () => statusFilter.selectedKey.value,
  set: statusFilter.select,
})

const query = useListingRequestsPageQuery(page, statusFilter.statuses)
const countsQuery = useListingRequestStatusCountsQuery()

// A failed counts read shows dashes rather than zeroes.
const filterCards = computed(() =>
  LISTING_STATUS_KPIS.map((kpi) => ({
    key: kpi.key,
    label: kpi.label,
    value: countsQuery.data.value ? countListingStatusKpi(kpi, countsQuery.data.value) : null,
  })),
)

const rows = computed(() => query.data.value?.rows ?? [])
// Last pagination meta that loaded. A failed page fetch has no data, so without
// this the pager would vanish with it and strand the seller on the broken page.
const lastPagination = ref<ListingRequestListPage['pagination']>()
const pagination = computed(() => query.data.value?.pagination ?? lastPagination.value)

const normalisedSearch = computed(() => searchTerm.value.trim().toLowerCase())

const visibleRows = computed(() => {
  const term = normalisedSearch.value
  if (!term) return rows.value
  return rows.value.filter((row) =>
    [row.propertyName, row.requestRefCode, row.buildingName, row.locationArea].some((field) =>
      field?.toLowerCase().includes(term),
    ),
  )
})

const isFirstLoad = computed(() => query.isPending.value)
const isListEmpty = computed(
  () => !query.isPending.value && !query.isError.value && (pagination.value?.totalItems ?? 0) === 0,
)
// Only an unfiltered empty list means the seller has never submitted anything.
const hasNoRequests = computed(() => isListEmpty.value && !isFiltered.value)
const hasNoStatusMatches = computed(() => isListEmpty.value && isFiltered.value)
const hasNoMatches = computed(
  () => !hasNoRequests.value && rows.value.length > 0 && visibleRows.value.length === 0,
)
const hasError = computed(() => query.isError.value && rows.value.length === 0)

const listState = computed<ListTableState>(() => {
  if (isFirstLoad.value) return 'loading'
  if (hasError.value) return 'error'
  return hasNoStatusMatches.value || hasNoMatches.value ? 'empty' : 'ready'
})
const tablePagination = computed(() =>
  pagination.value
    ? { ...pagination.value, page: page.value, pageSize: LISTINGS_PAGE_LIMIT }
    : null,
)

// A page can vanish underneath the seller (a withdrawn request shrinks the last
// page) — step back to the new last page instead of rendering an empty table.
watch(pagination, (meta) => {
  if (meta) lastPagination.value = meta
  if (meta && meta.totalPages > 0 && page.value > meta.totalPages) page.value = meta.totalPages
})

function changePage(next: number) {
  page.value = next
}

function clearSearch() {
  searchTerm.value = ''
}

function goToDetail(id: string) {
  void router.push(routes.LISTING_DETAIL.replace(':id', id))
}

function goToNew() {
  void router.push(routes.LISTING_NEW)
}

function retry() {
  void query.refetch()
}

const { draftPendingDiscard, isDiscarding, askToDiscard, cancelDiscard, confirmDiscard } =
  useDraftDiscard()
</script>

<template>
  <Box direction="col" gap="4">
    <Card v-if="hasNoRequests" variant="outlined" padding="6">
      <EmptyState
        icon="home"
        title="No sell requests yet"
        description="Start by submitting a sell request — track its review, agreement, and status here."
        primary-action-label="New Sell Request"
        primary-action-icon="plus"
        @primary-action="goToNew"
      />
    </Card>

    <template v-else>
      <FilterCardRow
        v-model="selectedKpi"
        :cards="filterCards"
        :loading="countsQuery.isPending.value"
        group-label="Filter by status"
      />

      <ListTableCard
        v-model:search="searchTerm"
        :title="statusFilterLabel"
        :state="listState"
        :columns="LISTINGS_TABLE_COLUMNS"
        :grid-template="LISTINGS_GRID_TEMPLATE"
        :pagination="tablePagination"
        item-label="request"
        items-label="requests"
        search-placeholder="Search by name, reference or location"
        error-message="We couldn't load your sell requests. Check your connection and try again."
        :is-search-disabled="isFirstLoad"
        :is-refreshing="query.isPlaceholderData.value"
        @update:page="changePage"
        @retry="retry"
      >
        <template #actions>
          <Button label="New Sell Request" color="primary" icon="plus" @click="goToNew" />
        </template>

        <template #empty>
          <EmptyState
            v-if="hasNoStatusMatches"
            size="compact"
            icon="filter"
            :title="`No requests in “${statusFilterLabel}”`"
            description="None of your sell requests are at this status right now."
            secondary-action-label="Show all requests"
            @secondary-action="clearStatusFilter"
          />
          <EmptyState
            v-else
            size="compact"
            icon="search"
            title="No matching requests on this page"
            :description="`Nothing on page ${page} matches “${searchTerm.trim()}”. Clear the search or check another page.`"
            secondary-action-label="Clear search"
            @secondary-action="clearSearch"
          />
        </template>

        <ListingRequestRow
          v-for="request in visibleRows"
          :key="request.id"
          :request="request"
          @view="goToDetail"
          @discard="askToDiscard"
        />
      </ListTableCard>
    </template>
  </Box>

  <ConfirmLeaveModal
    :visible="draftPendingDiscard !== null"
    title="Discard this draft?"
    message="The details you saved will be deleted. You can always start a new sell request."
    confirm-label="Discard"
    cancel-label="Keep draft"
    :saving="isDiscarding"
    @confirm="confirmDiscard"
    @cancel="cancelDiscard"
  />
</template>
